import Cookies from 'js-cookie';

export interface User {
  id: string;
  username: string;
  compCd?: string;
  exp?: number;
}

export interface TokenClaims {
  iss: string;
  sub: string;
  iat: number;
  exp: number;
  uid: number;
  loginId: string;
  username: string;
  roles: string[];
  companyCode: string;
  organizationCode: string;
  organizationName: string;
  roleName?: string; // 마이그레이션용 - 더 이상 사용하지 않음
}

export interface UserRole {
  roles: string[];
  organizationCode: string;
  organizationName: string;
  companyCode: string;
  isSuperAdmin: boolean;
  userLevel: 'COMPANY' | 'ORGANIZATION' | 'BUSINESS' | 'STORE';
  // 편의 함수들
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

export const AUTH_COOKIE_NAME = 'auth-tokens';
export const ACCESS_TOKEN_KEY = 'access-token';
export const REFRESH_TOKEN_KEY = 'refresh-token';

// 브라우저 호환 Base64 URL 디코딩 함수
function base64UrlDecode(str: string): string {
  // Base64 URL을 Base64로 변환
  let base64 = str.replace(/-/g, '+').replace(/_/g, '/');

  // 패딩 추가
  const pad = base64.length % 4;
  if (pad) {
    if (pad === 1) {
      throw new Error('Invalid base64url string');
    }
    base64 += new Array(5 - pad).join('=');
  }

  try {
    // 브라우저의 atob 함수 사용 + UTF-8 디코딩
    const bytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  } catch {
    throw new Error('Invalid base64url string');
  }
}

// 브라우저 호환 JWT 디코딩 함수
function decodeJWT(token: string): any {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      throw new Error('Invalid JWT format');
    }

    const payload = parts[1];
    const decodedPayload = base64UrlDecode(payload);
    return JSON.parse(decodedPayload);
  } catch (error) {
    console.error('JWT decode error:', error);
    return null;
  }
}

export function saveAuthTokens(tokens: AuthTokens) {
  if (typeof window !== 'undefined') {
    localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
  }

  Cookies.set(AUTH_COOKIE_NAME, JSON.stringify(tokens), {
    expires: 7, // 7 days
    secure: import.meta.env.PROD,
    sameSite: 'strict',
  });
}

export function getAuthTokens(): AuthTokens | null {
  try {
    // Check if we're in a browser environment
    if (typeof window === 'undefined') {
      return null;
    }

    const cookieTokens = Cookies.get(AUTH_COOKIE_NAME);
    if (cookieTokens) {
      return JSON.parse(cookieTokens);
    }

    const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

    if (accessToken && refreshToken) {
      return { accessToken, refreshToken };
    }

    return null;
  } catch {
    return null;
  }
}

export function clearAuthTokens() {
  if (typeof window !== 'undefined') {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  }
  Cookies.remove(AUTH_COOKIE_NAME);
}

export function decodeAccessToken(token: string): User | null {
  try {
    const decoded = decodeJWT(token);
    if (!decoded) return null;

    return {
      id: decoded.sub || decoded.id,
      username: decoded.username || decoded.name || decoded.preferred_username || decoded.sub,
      compCd: decoded.companyCode || decoded.company_code || decoded.compCd,
      exp: decoded.exp,
    };
  } catch {
    return null;
  }
}

export function decodeTokenClaims(token: string): TokenClaims | null {
  try {
    const decoded = decodeJWT(token);
    if (!decoded) return null;

    // Keycloak 토큰 형식과 기존 커스텀 JWT 형식 모두 지원
    return {
      iss: decoded.iss,
      sub: decoded.sub,
      iat: decoded.iat,
      exp: decoded.exp,
      uid: decoded.uid || 0, // Keycloak 토큰에는 uid 없음 - 백엔드에서 DB로 해결
      loginId: decoded.loginId || decoded.preferred_username,
      username: decoded.username || decoded.name || decoded.preferred_username,
      roles: decoded.roles || decoded.realm_access?.roles || [],
      companyCode: decoded.companyCode || decoded.company_code || '',
      organizationCode: decoded.organizationCode || decoded.org_code || '',
      organizationName: decoded.organizationName || decoded.organization_name || '',
      roleName: decoded.roleName, // 마이그레이션용
    };
  } catch {
    return null;
  }
}

export function getUserRole(token: string): UserRole | null {
  try {
    const claims = decodeTokenClaims(token);
    if (!claims) return null;

    const roles = claims.roles || [];
    const isSuperAdmin = roles.includes('SUPER_ADMIN');

    // 역할을 기반으로 사용자 레벨 결정
    let userLevel: 'COMPANY' | 'ORGANIZATION' | 'BUSINESS' | 'STORE' = 'STORE';

    if (isSuperAdmin) {
      userLevel = 'COMPANY';
    } else if (
      roles.includes('ADMIN') ||
      roles.includes('COMPANY_ADMIN') ||
      roles.includes('ORGANIZATION_ADMIN')
    ) {
      userLevel = 'COMPANY'; // ADMIN 역할도 COMPANY 레벨로 처리
    } else if (roles.includes('BUSINESS_ADMIN')) {
      userLevel = 'BUSINESS';
    } else if (roles.includes('STORE_ADMIN') || roles.includes('STORE_USER')) {
      userLevel = 'STORE';
    } else {
      // 기본적으로 관리자 계정은 COMPANY 레벨로 처리
      userLevel = 'COMPANY';
    }

    // 편의 함수들
    const hasRole = (role: string): boolean => roles.includes(role);
    const hasAnyRole = (checkRoles: string[]): boolean =>
      checkRoles.some(role => roles.includes(role));

    return {
      roles,
      organizationCode: claims.organizationCode,
      organizationName: claims.organizationName,
      companyCode: claims.companyCode,
      isSuperAdmin,
      userLevel,
      hasRole,
      hasAnyRole,
    };
  } catch {
    return null;
  }
}

export function isTokenExpired(token: string): boolean {
  try {
    const decoded = decodeAccessToken(token);
    if (!decoded || !decoded.exp) return true;

    const now = Math.floor(Date.now() / 1000);
    return decoded.exp < now;
  } catch (error) {
    return true;
  }
}

export function getAccessToken(): string | null {
  const tokens = getAuthTokens();
  if (!tokens) return null;

  if (isTokenExpired(tokens.accessToken)) {
    return null;
  }

  return tokens.accessToken;
}

export function getCurrentUser(): User | null {
  const accessToken = getAccessToken();
  if (!accessToken) return null;

  return decodeAccessToken(accessToken);
}

export function getCurrentUserRole(): UserRole | null {
  const accessToken = getAccessToken();
  if (!accessToken) return null;

  return getUserRole(accessToken);
}

export function getCurrentTokenClaims(): TokenClaims | null {
  const accessToken = getAccessToken();
  if (!accessToken) return null;

  return decodeTokenClaims(accessToken);
}

export function getCurrentUserId(): number | null {
  const claims = getCurrentTokenClaims();
  return claims?.uid || null;
}
